import type { Language } from '@eformulieren/engine'
import type { Entries } from '@eformulieren/model'
import { BusyButton } from '@eformulieren/renderer'
import { type ReactNode, useEffect, useState } from 'react'
import { getEntries, targetFromPage } from './login.js'
import { logOut, logoutAddress } from './logout.js'

/**
 * "Uitloggen" voor wie hier werkt: invuller, toetser, medeschrijver of adviseur (R-154, ontwerp 3.3).
 *
 * Wist bij de inlogdienst de sessie en de podcookies (`POST /:gastheer/uitloggen`), en gaat dan naar de
 * pagina na het uitloggen. Die zegt dat wat er is ingevuld is opgeslagen, en biedt opnieuw inloggen,
 * een ander account, en (als de ingang het kent) uitloggen bij de identiteitsprovider.
 *
 * **Niet tonen wat niet werkt.** Zonder de ingangen van de pod, of als de ingang van de persoon er niet
 * bij staat (een token van een gastheer in een header), staat er geen knop.
 */
export function LogoutButton({ taal: language, persoon, ingangen: given }: { readonly taal: Language; readonly persoon: string | undefined; readonly ingangen?: Entries }): ReactNode {
  const [entries, setEntries] = useState<Entries | undefined>(given)
  useEffect(() => {
    if (given !== undefined) {
      setEntries(given)
      return
    }
    let current = true
    void getEntries().then((read) => {
      if (current) setEntries(read)
    })
    return () => {
      current = false
    }
  }, [given])
  const address = entries === undefined || persoon === undefined ? undefined : logoutAddress(entries, persoon)
  if (address === undefined) return null
  return (
    <BusyButton
      className="ef-actieknop pod-uitloggen"
      data-soort="secundair"
      data-proef="uitloggen"
      busyText={T.bezig[language]}
      errorText={T.mislukt[language]}
      onAction={async () => {
        const next = await logOut(address, targetFromPage(window.location))
        window.location.assign(next)
      }}
    >
      {T.knop[language]}
    </BusyButton>
  )
}

const T = {
  knop: { nl: 'Uitloggen', en: 'Sign out' },
  bezig: { nl: 'Bezig met uitloggen…', en: 'Signing out…' },
  mislukt: { nl: 'Uitloggen lukt nu niet. Probeer het opnieuw.', en: 'Signing out is not possible right now. Please try again.' },
} as const
